import React, { useRef, useState, useMemo, useEffect } from "react";
import SecondaryUnit from "./SecondaryUnit";
import CustomUnitAdd from "./CustomUnitAdd";
import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import { AddIcon, XIcon } from "../../../constants/Icons";

const Unit = ({ onClose, unitSuggest = [], onAdd,secondaryUnitData }) => {
  const searchInputRef = useRef();
  const listRef = useRef();
  const newUnitBtnRef = useRef();
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // const [secondaryOpen, setSecondaryOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [units, setUnits] = useState(unitSuggest);
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  
  //  Always derive sorted + filtered list from original data

  const normalizedUnits = useMemo(() => {
    if (!units) return { store: [], default: [] };
  
    const store =
      units.storeUnits?.map((u) => ({
        primaryUnitName: u?.unitName || u?.primaryUnitName,
        primaryUnitCode: u?.unitCode || u?.primaryUnitCode,
        type: "store",
        }))
        .filter(
          (u) =>
            !(
              u?.primaryUnitCode === secondaryUnitData?.primaryUnitCode &&
              u?.primaryUnitName === secondaryUnitData?.primaryUnitName
            )
        ) || [];
  
    const defaults =
      units.defaultUnits?.map((u) => ({
        primaryUnitName: u?.unitName,
        primaryUnitCode: u?.unitCode,
        secondaryUnitCode: u?.defaultSecondaryunitCodeNormalized,
        type: "default",
        }))
        .filter(
          (u) =>
            !(
              u?.primaryUnitCode === secondaryUnitData?.primaryUnitCode &&
              u?.primaryUnitName === secondaryUnitData?.primaryUnitName
            )
        ) || [];

    return { store, default: defaults };
  }, [units, secondaryUnitData]);

  const filteredSuggestions = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();

    const filterFn = (item) =>
      !searchTerm ||
      item?.primaryUnitName?.toLowerCase().includes(searchLower) ||
      item?.primaryUnitCode?.toLowerCase().includes(searchLower);

    const store = normalizedUnits.store
      .filter(filterFn)
      .sort(
        (a, b) =>
          (a?.primaryUnitName || "").localeCompare(b?.primaryUnitName) || ""
      );

    const defaults = normalizedUnits.default
      .filter(filterFn)
      .sort((a, b) =>
        (a?.primaryUnitName || "").localeCompare(b?.primaryUnitName || "")
      );

    return { store, defaults };
  }, [normalizedUnits, searchTerm]);

  const handleAddUnitSubmit = (item) => {
    const payload = {
      primaryUnitName: item?.primaryUnitName,
      primaryUnitCode: item?.primaryUnitCode,
    };

    if (item?.secondaryUnitCode) {
      payload.secondaryUnitName = item?.secondaryUnitName;
      payload.secondaryUnitCode = item?.secondaryUnitCode;
      payload.conversion = item?.conversion;
    }

    onAdd(payload);
  };

  // const handleSecondaryUnitOpen = () => {
  //   setSecondaryOpen(true);
  // };

  // const handleAddSecondary = (data) => {
  //   console.log(data);
  // };

  const handleCustomData = async (data) => {
    const newUnit = {
      primaryUnitName: data?.name,
      primaryUnitCode: data?.code,
    };

    await handleAddCustomUnit(newUnit);
  };

  const handleAddCustomUnit = async (formData) => {
    try {
      if (!checkNetwork()) return;

      const response = await AxiosInstance.post("module/unit", formData);
      const results = response.data.results;

      setUnits((prev) => ({
        ...prev,
        storeUnits: [...(prev?.storeUnits || []), results],
      }));

      setCustomOpen(false);

      onAdd(results);
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const noData =
  filteredSuggestions.store.length === 0 &&
  filteredSuggestions.defaults.length === 0;

  useEffect(() => {
    setUnits(unitSuggest);
  }, [unitSuggest]);

  const combinedSuggestions = useMemo(() => {
    return [...filteredSuggestions.store, ...filteredSuggestions.defaults];
  }, [filteredSuggestions]);
  
 const scrollToItem = (index) => {
  const list = listRef.current;
  if (!list) return;

  const item = list.children[index];
  if (item) {
    item.scrollIntoView({
      block: "nearest",
    });
  }
};

const handleKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
  if (!combinedSuggestions.length) return;

    e.preventDefault();
  if (e.key === "ArrowDown") {
 
    setHighlightedIndex((prev) => {
      const next = prev < combinedSuggestions.length - 1 ? prev + 1 : 0;

      scrollToItem(next);
      return next;
    });
  }

  if (e.key === "ArrowUp") {
    
    setHighlightedIndex((prev) => {
      const next = prev > 0 ? prev - 1 : combinedSuggestions.length - 1;

      scrollToItem(next);
      return next;
    });
  }}

  if (e.key === "Enter") {
  e.preventDefault();
  e.stopPropagation();

  //  Case 1: highlighted item exists
  if (highlightedIndex >= 0 && combinedSuggestions[highlightedIndex]) {
    handleAddUnitSubmit(combinedSuggestions[highlightedIndex]);
    return;
  }

  //  Case 2: no highlight but list has data → pick first
  if (combinedSuggestions.length > 0) {
    handleAddUnitSubmit(combinedSuggestions[0]);
    return;
  }

  //  Case 3: no data → focus New Unit button
  if (noData) {
    setCustomOpen(true);
  }
}
};

useEffect(() => {
  if (combinedSuggestions.length > 0) {
    setHighlightedIndex(0);
  } else {
    setHighlightedIndex(-1);
  }
}, [searchTerm, combinedSuggestions]);

  return (
    <div className="modal-overlay">
      <div className="modal-backdrop show"></div>
      <div
        className="modal show"
        style={{ display: "block" }}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title">Unit</h6>
              <button className="btn-close" onClick={onClose}></button>
            </div>

            <div className="modal-body bg-light">
              <div className="card mb-0">
                <div className="card-body">
                  <div className="row g-3 align-items-center">
                    {/* Search */}
                    <div className="col-8">
                      <input
                        type="text"
                        className="form-control"
                        ref={searchInputRef}
                        autoFocus
                        autoComplete="off"
                        placeholder="Search by primary unit name..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setHighlightedIndex(-1);
                        }}
                        onKeyDown={handleKeyDown} 
                      />
                    </div>

                    <div className="col-4">
                      <div className="btn-group text-right align-items-center">
                        <button
                          ref={newUnitBtnRef}
                          className="btn btn-unit text-right w-100"
                          onClick={() => setCustomOpen(true)}
                          onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                  e.preventDefault();
                                  setCustomOpen(true);
                              }
                            }}
                        >
                          <AddIcon iClass={"fs-15"}/> New Unit
                        </button>
                      </div>
                    </div>

                    {/* Suggestions */}
                    <div className="col-12">
                      <ul   ref={listRef} className="suggestions-unitlist" 
                      style={{minHeight:'283px', overflowY: "auto" }}>
                        {!noData && (
                          <>
                        {filteredSuggestions.store.length > 0 && (
                          <>
                            {/* <li className="unit-group-title">Store Units</li> */}

                            {filteredSuggestions.store.map((item, index) => (
                              <li
                                key={item.primaryUnitCode}
                                className={`suggestion-item ${
                                highlightedIndex === index ? "active-suggestion" : ""
                              }`}
                                onClick={() => handleAddUnitSubmit(item)}
                              >
                                <div style={{ fontWeight: 600 }}>
                                  {item.primaryUnitName}
                                  <small className="text-muted ms-2">
                                    ({item.primaryUnitCode})
                                  </small>
                                </div>
                              </li>
                            ))}
                          </>
                        )}

                        {filteredSuggestions.defaults.length > 0 && (
                          <>
                            {/* <li className="unit-group-title">Default Units</li> */}

                            {filteredSuggestions.defaults.map((item, index) => (
                              <li
                                key={item.primaryUnitCode}
                                 className={`suggestion-item ${
                                    highlightedIndex === filteredSuggestions.store.length + index ? "active-suggestion" : ""
                                  }`}
                                onClick={() => handleAddUnitSubmit(item)}
                              >
                                <div style={{ fontWeight: 600 }}>
                                  {item.primaryUnitName}
                                  <small className="text-muted ms-2">
                                    ({item.primaryUnitCode})
                                  </small>
                                </div>

                                {/* {item.secondaryUnitCode && (
                                  <div className="font-11">
                                    Secondary ({item.secondaryUnitCode})
                                  </div>
                                )} */}
                              </li>
                            ))}
                          </>
                        )}
                        </>
                        )}
                        
                        {noData && (
                          <li className="suggestion-item text-center">
                            <div className="mb-2 text-muted">No unit found</div>

                            <button
                              className="btn btn-sm btn-unit"
                              onClick={() => setCustomOpen(true)}
                            >
                              <AddIcon iClass={"fs-15"}/> New Unit
                            </button>
                          </li>)}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer d-block">
              <button
                type="button"
                className="btn btn-outline-secondary text-left"
                onClick={onClose}
              >
                <XIcon/> Close
              </button>
              {/* <div className="btn-group text-right align-items-center">
                <button
                  onClick={handleSecondaryUnitOpen}
                  className="btn btn-unit text-right"
                >
                  <i className="ng ng-ng-add-icon fs-15"></i> Add Secondary Unit
                </button>
              </div> */}
            </div>
          </div>
        </div>
      </div>
      {/* {secondaryOpen && (
        <SecondaryUnit
          onClose={() => setSecondaryOpen(false)}
          unitSuggest={unitSuggest}
          onAdd={handleAddSecondary}
        />
      )} */}
      {customOpen && (
        <CustomUnitAdd
          onclose={() => {setCustomOpen(false);
            searchInputRef.current.focus();}}
          onSave={handleCustomData}
        />
      )}
    </div>
  );
};

export default Unit;
